import clsx from "clsx";
import { FC, Fragment, useEffect } from "react";

import { InfiniteTileScroll } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/InfiniteTileScroll/InfiniteTileScroll";
import { PromoItemizedBillTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/PromoItemizedBillTile/PromoItemizedBillTile";
import { PurchaseIntentTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/PurchaseIntentTile/PurchaseIntentTile.tsx";
import { SkeletonTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/SkeletonTile/SkeletonTile";
import {
  getMetaTiles,
  mapDevice,
} from "@/components/Portal/components/PortalDeviceTiles/utils/tileHelpers.tsx";
import { ExtendedSimCardDetail } from "@/hooks/useExtendedSims";
import { BasketSummaryResponse, SimCardDetail } from "@/services/model";
import { TransformedProducts } from "@/utils/tariffUtils.ts";

interface TilesGridProps {
  flatSimList: SimCardDetail[];
  flatExtendedSimList: ExtendedSimCardDetail[];
  basketItems?: BasketSummaryResponse[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isError: boolean;
  shouldShowItemizedBillPromoCard: boolean;
  showMetaTiles: boolean; // Show meta tiles (Add Device, Upsell)
  showSims?: boolean; // Whether to show SIM tiles (default: true)
  showPurchaseIntents?: boolean; // Whether to show purchase intent tiles (default: false)
  addDeviceRoute?: string;
  needsIdCheck: boolean | undefined; // Whether the user needs an ID check
  manageRoute: string;
  pageSize: number;
  useInfiniteScroll?: boolean; // Whether to use infinite scrolling (default: true)
  fetchAllPages?: boolean; // Fetch all pages when infinite scroll is disabled (default: true)
  maxTiles?: number; // Limit the number of SIM tiles rendered
  autoHeight?: boolean; // Drop the grid's reserved minimum height (default: false)
  processedOptions?: TransformedProducts; // Pre-fetched options
}

/**
 * Component that renders the grid of device tiles, including loading skeletons,
 * infinite scroll functionality, and meta tiles.
 */
export const TilesGrid: FC<TilesGridProps> = ({
  flatSimList,
  flatExtendedSimList,
  basketItems,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  isError,
  shouldShowItemizedBillPromoCard,
  showMetaTiles,
  showSims = true,
  showPurchaseIntents = false,
  addDeviceRoute,
  needsIdCheck,
  manageRoute,
  pageSize,
  useInfiniteScroll = true,
  fetchAllPages = true,
  maxTiles,
  autoHeight = false,
  processedOptions,
}) => {
  const displayedSimList = maxTiles
    ? flatSimList.slice(0, maxTiles)
    : flatSimList;
  const displayedExtendedSimList = maxTiles
    ? flatExtendedSimList.slice(0, maxTiles)
    : flatExtendedSimList;
  const disableInfiniteFetching = !!maxTiles;

  useEffect(() => {
    if (
      !useInfiniteScroll &&
      fetchAllPages &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    useInfiniteScroll,
    fetchAllPages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const gridClassName = clsx(
    "tiles-grid",
    autoHeight && "tiles-grid--auto-height",
  );

  if (isLoading) {
    return (
      <div className={gridClassName}>
        <SkeletonTile />
        <SkeletonTile />
        <SkeletonTile />
      </div>
    );
  }

  const content = (
    <div data-testid={"tiles-container"} className={gridClassName}>
      {showPurchaseIntents &&
        basketItems?.map((basket) => (
          <PurchaseIntentTile
            key={basket.basketId}
            intent={basket}
            processedOptions={processedOptions}
          />
        ))}
      {displayedSimList && (
        <>
          {shouldShowItemizedBillPromoCard && <PromoItemizedBillTile />}
          {showSims &&
            displayedExtendedSimList.map((device, i) => (
              <Fragment key={device.simId}>
                {mapDevice(device, needsIdCheck, manageRoute)}
                {/* Show the meta tiles if:
              - only one page and last sim reached
              - or more than one page and end of first page reached
              - or more than two pages and end of list reached */}
                {showMetaTiles &&
                  addDeviceRoute &&
                  ((displayedSimList.length <= pageSize &&
                    displayedSimList.length - 1 === i) ||
                    (displayedSimList.length > pageSize &&
                      pageSize - 1 === i) ||
                    (displayedSimList.length > pageSize * 2 &&
                      displayedSimList.length - 1 === i)) && (
                    <>{getMetaTiles(addDeviceRoute)}</>
                  )}
              </Fragment>
            ))}
          {!showSims && showMetaTiles && addDeviceRoute && (
            <>{getMetaTiles(addDeviceRoute)}</>
          )}
        </>
      )}
      {/* If there are no sims or purchase intents, show meta tiles */}
      {showSims &&
        displayedSimList.length === 0 &&
        (!showPurchaseIntents || (basketItems?.length ?? 0) === 0) &&
        showMetaTiles &&
        addDeviceRoute && <>{getMetaTiles(addDeviceRoute)}</>}
    </div>
  );

  if (!useInfiniteScroll) {
    return content;
  }

  return (
    <InfiniteTileScroll
      items={displayedSimList}
      hasNextPage={disableInfiniteFetching ? false : !!hasNextPage}
      loadingNextPage={isLoading || isFetchingNextPage}
      error={isError}
      fetchNextPage={fetchNextPage}
      disableAutoFetch={disableInfiniteFetching}
      noMoreDataChild={""}
    >
      {content}
    </InfiniteTileScroll>
  );
};
